import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Environment } from 'src/environments/environment.model';

import { ApiNotFoundErrorResponse } from '../../../shared/models';
import {
  AvailableBemCropVariety,
  ChangeCropVarietyRequest,
  ChangePlantDateRequest,
  ChangePlantDateResponse,
  CropFieldNutrientCalculationsResponse,
  GetCropFieldBemResponse,
  GetCropFieldTrendLineResponse,
  GetNutrientAdviceBemResponse,
  GetSoilCropAnalysesResponse,
  IsBemCropFieldDto,
  IsBemCustomerDto,
} from '../models/models';

@Injectable({
  providedIn: 'root',
})
export class BemApiService {
  protected http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  protected bemBaseUrl = this.env.APIM_BASE_URL + '/arable-advice-service/';

  getCropFieldBem(id: number): Observable<GetCropFieldBemResponse> {
    return this.http.get<GetCropFieldBemResponse>(`${this.bemBaseUrl}crop-fields/${id}/bem`).pipe(
      catchError(this.handleApiError),
    );
  }

  changePlantDate(plantDateData: ChangePlantDateRequest): Observable<ChangePlantDateResponse> {
    return this.http.patch<ChangePlantDateResponse>(`${this.bemBaseUrl}crop-fields/${plantDateData.cropFieldId}/plant-date`, plantDateData);
  }

  getAvailableCropVarieties(cropFieldId: number): Observable<AvailableBemCropVariety[]> {
    return this.http.get<AvailableBemCropVariety[]>(`${this.bemBaseUrl}crop-fields/${cropFieldId}/available-crop-varieties`);
  }

  changeCropVariety(request: ChangeCropVarietyRequest): Observable<void> {
    return this.http.patch<void>(`${this.bemBaseUrl}crop-fields/${request.cropFieldId}/bem/crop-variety`, {
      cropVarietyId: request.cropVarietyId,
    });
  }

  getCropFieldNutrientCalculation(year: number): Observable<CropFieldNutrientCalculationsResponse[]> {
    return this.http.get<CropFieldNutrientCalculationsResponse[]>(`${this.bemBaseUrl}nutrient-calculation/year/${year}`);
  }

  getNutrientAdviceBem(cropFieldId: number): Observable<GetNutrientAdviceBemResponse> {
    return this.http.get<GetNutrientAdviceBemResponse>(
      `${this.bemBaseUrl}crop-fields/${cropFieldId}/nutrient-advice/bem`,
    ).pipe(
      catchError(this.handleApiError),
    );
  }

  getSoilCropAnalyses(cropFieldId: number): Observable<GetSoilCropAnalysesResponse> {
    return this.http.get<GetSoilCropAnalysesResponse>(`${this.bemBaseUrl}crop-fields/${cropFieldId}/soil-crop-analyses`).pipe(
      catchError(this.handleApiError),
    );
  }

  getCropFieldTrendLine(cropFieldId: number): Observable<GetCropFieldTrendLineResponse> {
    return this.http.get<GetCropFieldTrendLineResponse>(
      `${this.bemBaseUrl}crop-fields/${cropFieldId}/trendline`,
    ).pipe(
      catchError(this.handleApiError),
    );
  }

  isBemCustomer(year: number): Observable<IsBemCustomerDto> {
    return this.http.get<IsBemCustomerDto>(`${this.bemBaseUrl}advices/${year}/is-bem-customer`).pipe(
      catchError(() => of({ isBemCustomer: false })),
    );
  }

  getIsBemCropField(cropFieldId: number): Observable<IsBemCropFieldDto> {
    return this.http.get<IsBemCropFieldDto>(`${this.bemBaseUrl}crop-fields/${cropFieldId}/bem/is-participating`).pipe(
      catchError(() => of({ isParticipating: false })),
    );
  }

  private handleApiError(error: HttpErrorResponse) {
    return of({
      status: error?.error?.status || error?.status || 500,
      title: error?.error?.title || error?.message || 'Unknown Error',
      type: error?.error?.type || error?.type || 'Unknown',
      traceId: error?.error?.traceId || 'N/A',
    } as ApiNotFoundErrorResponse);
  }
}
